import { createServiceRequest } from '@/services/http/createServiceRequest';
import type { ApiRequestOptions } from './types/common';

const notificationRequest = createServiceRequest('notification');

export type MessageReadStatus = 'read' | 'unread';
export type MessagePriority = 'low' | 'normal' | 'high' | 'critical';
export type MessageStatus = 'success' | 'failed' | 'pending';

export type NotificationCategory = {
  id: number;
  uuid: string;
  tenantUuid: string;
  code: string;
  name: string;
  parentCode?: string | null;
  description?: string | null;
  sortOrder: number;
  icon?: string | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  children?: NotificationCategory[];
};

export type NotificationMessage = {
  id: number;
  uuid: string;
  tenantUuid: string;
  categoryCode?: string | null;
  title: string;
  summary?: string | null;
  content?: string | null;
  link?: string | null;
  status: MessageStatus;
  priority: MessagePriority;
  target?: Record<string, unknown> | null;
  errorMessage?: string | null;
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
};

export type NotificationInboxQuery = {
  page?: number;
  limit?: number;
  categoryCode?: string;
  keyword?: string;
  readStatus?: MessageReadStatus;
};

export type NotificationInboxResponse = {
  list: NotificationMessage[];
  total: number;
  page: number;
  limit: number;
};

export type NotificationUnreadCountMap = Record<string, number>;

export async function getNotificationCategories(options?: ApiRequestOptions) {
  return notificationRequest<NotificationCategory[]>('/bff/categories', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getNotificationInbox(
  query: NotificationInboxQuery,
  options?: ApiRequestOptions,
) {
  return notificationRequest<NotificationInboxResponse>('/bff/messages/inbox', {
    method: 'GET',
    params: query,
    ...(options || {}),
  });
}

export async function getNotificationUnreadCounts(options?: ApiRequestOptions) {
  return notificationRequest<NotificationUnreadCountMap>(
    '/bff/messages/inbox/unread-count',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function getNotificationMessageDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return notificationRequest<NotificationMessage>(`/bff/messages/inbox/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function markNotificationMessageRead(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return notificationRequest<void>(`/bff/messages/inbox/${uuid}/read`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function markNotificationMessageUnread(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return notificationRequest<void>(`/bff/messages/inbox/${uuid}/unread`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function batchReadNotificationMessages(
  uuids: string[],
  options?: ApiRequestOptions,
) {
  return notificationRequest<{ updated: number }>('/bff/messages/inbox/batch-read', {
    method: 'POST',
    data: { uuids },
    ...(options || {}),
  });
}

export async function readAllNotificationMessages(
  categoryCode?: string,
  options?: ApiRequestOptions,
) {
  return notificationRequest<{ updated: number }>('/bff/messages/inbox/read-all', {
    method: 'POST',
    params: categoryCode ? { categoryCode } : undefined,
    ...(options || {}),
  });
}

export async function batchDeleteNotificationMessages(
  uuids: string[],
  options?: ApiRequestOptions,
) {
  return notificationRequest<{ deleted: number }>(
    '/bff/messages/inbox/batch-delete',
    {
      method: 'POST',
      data: { uuids },
      ...(options || {}),
    },
  );
}
